import { DateTime } from 'luxon';

/**
 * Month-over-month movement in NEW records, shaped for a dashboard card badge.
 *
 * Replaces the old percentage badge, which was built on the analytics endpoint's
 * `newDeltaPercent` and carried no information at this data volume:
 *
 * - A percent change from an empty period is undefined, but the backend returns a
 *   hardcoded ±100 for it (`DashboardService.NewDeltaPercent`). On a low-traffic
 *   instance every card therefore showed 0% or ±100% — "0%" meaning *both*
 *   periods were empty, which reads as a flat trend instead of as no signal.
 * - It was computed over the request window, which the webapp sent as
 *   start-of-month minus 7 days: a span nobody can name that grows from 8 to 38
 *   days over the course of a month, so the "previous period" moved every day.
 * - Its source (`GetNewCountAsync`) means different things per module — records
 *   created in range for Customers/Products/Projects/Campaigns, but records in a
 *   pending *status* for Opportunities/Offers/Inspections/SalesOrders. A
 *   period-over-period percentage of a backlog is meaningless.
 *
 * This derives the badge from `metric.series` instead: the 12 monthly buckets of
 * created records that already draw the sparkline, built from the same base
 * filter as the card's total in every provider — so it means the same thing for
 * every module, and the badge agrees with the line above it.
 *
 * Absolute counts, not percentages: they are never undefined and never overstate.
 * "+1" is true whether the baseline was 0 or 400, where "+100%" is noise in the
 * first case and wrong in the second.
 */
export interface MonthlyDelta {
  /** Signed difference: current month minus previous month. */
  diff: number;
  /** Badge text with explicit sign ("+3", "-2", "0"). */
  label: string;
  icon: string;
  classes: string;
  /** Interpolation params for the `MONTHLY_DELTA_TOOLTIP` key. */
  tooltipParams: {
    current: number;
    previous: number;
    currentMonth: string;
    previousMonth: string;
  };
}

const TREND_ICON_UP = 'heroicons_solid:arrow-trending-up';
const TREND_ICON_DOWN = 'heroicons_solid:arrow-trending-down';
const TREND_ICON_FLAT = 'heroicons_solid:minus';

/** Emerald is reserved for a gain — see the coloring note in buildMonthlyDelta. */
const GAIN_INK = 'text-emerald-600 dark:text-emerald-400';
const NEUTRAL_INK = 'text-neutral-400 dark:text-neutral-600';

/**
 * Builds the badge from a monthly created-records series (oldest → newest). The
 * last bucket is the current calendar month, which the analytics request anchors
 * by sending today as its end date.
 *
 * Coloring is deliberately asymmetric: only a gain gets emerald, while a
 * shortfall stays neutral ink. The current month is still in progress, so
 * finishing above a full previous month is an earned result, but sitting below it
 * on the 4th says nothing yet — painting that red would be a false alarm every
 * time a month starts. The arrow and the signed number still carry the direction.
 *
 * @param series Monthly created-records buckets from the analytics endpoint.
 * @param lang Active i18n language, used for the month names in the tooltip.
 * @returns The badge, or null when the series is too short to compare.
 */
/**
 * Arrow and ink for a signed difference, so every trend badge in the app shares
 * one rule — including the ones that do not compare two months.
 *
 * The asymmetry is the point and is explained on `buildMonthlyDelta`: a gain
 * earns emerald, a shortfall stays neutral rather than turning red.
 *
 * @param diff Signed difference, in whatever unit the badge measures.
 */
export function deltaVisual(diff: number): { icon: string; classes: string } {
  return {
    icon: diff > 0 ? TREND_ICON_UP : diff < 0 ? TREND_ICON_DOWN : TREND_ICON_FLAT,
    classes: diff > 0 ? GAIN_INK : NEUTRAL_INK,
  };
}

export function buildMonthlyDelta(series: unknown, lang: string): MonthlyDelta | null {
  if (!Array.isArray(series) || series.length < 2) {
    return null;
  }

  const current = bucketCount(series, series.length - 1);
  const previous = bucketCount(series, series.length - 2);
  const diff = current - previous;

  return {
    diff,
    label: diff > 0 ? `+${diff}` : `${diff}`,
    icon: diff > 0 ? TREND_ICON_UP : diff < 0 ? TREND_ICON_DOWN : TREND_ICON_FLAT,
    classes: diff > 0 ? GAIN_INK : NEUTRAL_INK,
    tooltipParams: {
      current,
      previous,
      currentMonth: monthName(0, lang),
      previousMonth: monthName(-1, lang),
    },
  };
}

/** Bucket value as a count, treating anything non-numeric as no activity. */
function bucketCount(series: unknown[], index: number): number {
  const value = Number(series[index]);
  return Number.isFinite(value) ? value : 0;
}

/** Localized month name `offset` months from now (0 = the current month). */
function monthName(offset: number, lang: string): string {
  return DateTime.now().plus({ months: offset }).setLocale(lang).toFormat('LLLL');
}
